<template>
  <nav>
    <div class="logo-wrapper">
      <BaseImage :url="svgs.logo" />
      <img class="app-logo" :src="svgs.MOVITO_LOGO" />
    </div>

    <div v-if="!brf" class="app-switcher-wrapper">
      <AppSwitcherDropdown />
    </div>

    <p class="user-container">
      <template v-if="!account?.managerId">Inloggad som:</template>
      <template v-else>Inloggad som förvaltare:</template>
      <br />
      <span class="username">
        {{ account.firstName }} {{ account.lastName }}</span
      >
    </p>

    <div class="links-wrapper">
      <ul>
        <li v-for="item in activeMenu" :key="item.href">
          <a
            v-if="item.locked"
            href="#"
            class="link"
            @click.prevent="lockedModalOpen = true"
          >
            <div v-if="item.svg" class="icon">
              <svg
                :viewBox="item.svg.viewBox"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  :d="item.svg.path"
                  fill="#878b9d"
                  :fill-rule="item.svg.fillRule"
                />
              </svg>
            </div>
            <span>{{ item.label }}</span>
            <LockIcon class="lock-icon" />
          </a>
          <router-link
            v-else
            :to="item.href"
            class="link"
            :class="{
              disabled: isDisabledItem(item),
              active: isItemActive(item),
              'has-match': crossMatchCount(item) > 0
            }"
            @click="isDisabledItem(item) && $event.preventDefault()"
          >
            <div v-if="item.svg" class="icon">
              <svg
                :viewBox="item.svg.viewBox"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  :d="item.svg.path"
                  fill="#878b9d"
                  :fill-rule="item.svg.fillRule"
                />
              </svg>
            </div>
            <span>{{ item.label }}</span>
            <span v-if="crossMatchCount(item) > 0" class="cross-match-badge">
              {{ crossMatchCount(item) }}
            </span>
          </router-link>
        </li>
        <li v-for="item in sharedMenu" :key="item.href">
          <router-link
            :to="item.href"
            class="link"
            :class="{
              disabled: isDisabledItem(item),
              active: isItemActive(item)
            }"
            @click="isDisabledItem(item) && $event.preventDefault()"
          >
            <div v-if="item.svg" class="icon">
              <svg
                :viewBox="item.svg.viewBox"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  :d="item.svg.path"
                  fill="#878b9d"
                  :fill-rule="item.svg.fillRule"
                />
              </svg>
            </div>
            <span>{{ item.label }}</span>
          </router-link>
        </li>
      </ul>
    </div>

    <AccessInfoModal
      v-if="lockedModalOpen"
      :landlord-has-access="0"
      @close="lockedModalOpen = false"
    >
      <template #title>Analytics</template>
      <template #intro>
        Kul att ni är intresserade av <strong>Analytics</strong>!
      </template>
      <template #body>
        Analytics är inte tillgänglig än för
        <strong>{{ account.landlordName }}</strong
        >.
      </template>
    </AccessInfoModal>
  </nav>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import svgs from '@/utils/icon-constants';
import BaseImage from '@/components/BaseImage.vue';
import AppSwitcherDropdown from '@/components/AppSwitcherDropdown/AppSwitcherDropdown.vue';
import AccessInfoModal from '@/components/AccessInfoModal.vue';
import LockIcon from '@/components/AppSwitcherDropdown/LockIcon.vue';

export default {
  name: 'Menu',
  components: {
    BaseImage,
    AppSwitcherDropdown,
    AccessInfoModal,
    LockIcon
  },
  data() {
    return {
      svgs,
      lockedModalOpen: false
    };
  },
  computed: {
    ...mapState({
      account: state => state.app.account,
      sharedMenu: state => state.app.sharedMenu
    }),
    ...mapGetters({
      activeMenu: 'app/activeMenu',
      brf: 'app/brf',
      ahFilterMatchCounts: 'applications/ahFilterMatchCounts',
      ovFilterMatchCounts: 'applications/ovFilterMatchCounts',
      baFilterMatchCounts: 'applications/baFilterMatchCounts'
    }),
    isManager() {
      return !!this.account?.managerId;
    }
  },
  methods: {
    isDisabledItem(item) {
      if (!this.isManager) return false;
      return item.label === 'Statistik' || item.label === 'Inställningar';
    },
    isItemActive(item) {
      const path = this.$route.path;
      return path === item.href || path.startsWith(item.href + '/');
    },
    crossMatchCount(item) {
      // Visa räknare på "andra vyn" när sökning matchar där men vi står på annan vy
      if (this.isItemActive(item)) return 0;
      const path = this.$route.path;

      // Bytesansökan
      if (path.startsWith('/byten/alla') && item.href === '/byten/arkiv') {
        return this.baFilterMatchCounts.archived;
      }
      if (path.startsWith('/byten/arkiv') && item.href === '/byten/alla') {
        return this.baFilterMatchCounts.pending;
      }

      // Andrahand
      if (
        path.startsWith('/andrahand/alla') &&
        item.href === '/andrahand/arkiv'
      ) {
        return this.ahFilterMatchCounts.archived;
      }
      if (
        path.startsWith('/andrahand/arkiv') &&
        item.href === '/andrahand/alla'
      ) {
        return this.ahFilterMatchCounts.pending;
      }

      // Överlåtelser privat
      if (
        path.startsWith('/overlatelser-privat/alla') &&
        item.href === '/overlatelser-privat/arkiv'
      ) {
        return this.ovFilterMatchCounts.archived;
      }
      if (
        path.startsWith('/overlatelser-privat/arkiv') &&
        item.href === '/overlatelser-privat/alla'
      ) {
        return this.ovFilterMatchCounts.pending;
      }

      // Överlåtelser företag
      if (
        path.startsWith('/overlatelser-foretag/alla') &&
        item.href === '/overlatelser-foretag/arkiv'
      ) {
        return this.ovFilterMatchCounts.archived;
      }
      if (
        path.startsWith('/overlatelser-foretag/arkiv') &&
        item.href === '/overlatelser-foretag/alla'
      ) {
        return this.ovFilterMatchCounts.pending;
      }

      return 0;
    }
  }
};
</script>

<style scoped>
.logo-wrapper,
.app-switcher-wrapper {
  padding: 2rem 2rem 0 2.8rem;
}

.app-switcher-wrapper {
  width: 100%;
}

.app-logo {
  width: 95%;
  margin: 0 auto;
  display: block;
}

.user-container {
  margin-top: 4.2rem;
  margin-left: 3rem;
  font-weight: 400;
  font-size: clamp(1rem, 0.7vw, 1.2rem);
  text-align: left;
  line-height: 1.5;
}

.user-container .username {
  color: var(--color-dark-blue);
  font-weight: 600;
  font-size: clamp(1.2rem, 0.7vw, 1.4rem);
}

.links-wrapper {
  justify-content: center;
  margin-top: 47px;
}
.links-wrapper li {
  padding: 1rem 0;
}
.link {
  text-decoration: none;
  color: #878b9d;
  padding-left: 3rem;
  font-weight: 600;
  background-color: transparent;
  border: none;
  cursor: pointer;
  width: 100%;
  display: inline-block;
  text-align: left;
  font-size: clamp(1.2rem, 0.8vw, 1.4rem);
  display: flex;
  align-items: center;
}
.link span {
  margin-left: 1.5rem;
}

.link svg {
  display: block;
}

.lock-icon {
  fill: #878b9d;
  margin-left: 5px;
}
.link:hover {
  color: var(--color-blue);
}

.link:hover svg path {
  fill: var(--color-blue);
}
.link.active {
  color: var(--color-blue);
  border-left: 3px solid var(--color-blue);
  padding-left: 27px;
}
.link.active svg path {
  fill: var(--color-blue);
}
.link.disabled {
  opacity: 0.4;
  pointer-events: none;
  cursor: default;
}
.cross-match-badge {
  margin-left: auto;
  margin-right: 1.5rem;
  background: var(--color-blue);
  color: #fff;
  font-size: 1rem;
  font-weight: 700;
  min-width: 2rem;
  height: 2rem;
  padding: 0 0.6rem;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  animation: cross-match-pulse 1.7s infinite;
}
@keyframes cross-match-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(64, 159, 255, 0);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(64, 159, 255, 0.3);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(64, 159, 255, 0);
  }
}
.icon {
  height: 20px;
  width: 20px;
}
</style>
